import React, { useState, useEffect } from 'react';
import AccordionTile from '../Accordion';
import { CustomInput } from '../../../components/common';
import {
  useCreatePackageProduct,
  useUpdatePackageProduct,
  PackageProduct,
  Package,
  ProductTemplate,
  ProductTemplateKey,
} from '../../../hooks/useClients';

interface DiscountProps {
  packageData: Package;
  defaultTemplate: ProductTemplate;
  setSaveDataModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const Discount: React.FunctionComponent<DiscountProps> = ({
  packageData,
  defaultTemplate,
  setSaveDataModal,
}): JSX.Element => {
  const selectedPackageProduct = packageData.package_products.find(
    (item: PackageProduct) => item.product.key === 'DISCOUNT',
  );
  const { createPackageProduct } = useCreatePackageProduct(
    packageData.client_id!,
    selectedPackageProduct?.package_id! || packageData.id!,
    defaultTemplate.id,
  );
  const { updatePackageProduct } = useUpdatePackageProduct(packageData.client_id!, selectedPackageProduct?.id!);
  const [discount, setDiscount] = useState(0);
  const [discountTab, setDiscountTab] = useState(false);
  const [error, setError] = useState('');
  const [enableSaveOption, setEnableSaveOption] = useState(false);

  useEffect(() => {
    setSaveDataModal(enableSaveOption);
  }, [enableSaveOption]);

  const Validation = () => {
    if (discountTab) {
      if (!discount) {
        setError('Field is required');
        return false;
      } else if (discount.toString().indexOf('.') !== -1) {
        setError('Number may not include decimals - whole numbers only');
        return false;
      } else if (discount < 1 || discount > 100) {
        setError('Must be a number between 1 and 100');
        return false;
      }
    }
    setError('');
    return true;
  };

  useEffect(() => {
    if (packageData) {
      const packageProducts = packageData.package_products;

      const selectedItem = packageProducts.find((item: PackageProduct) => {
        if (item.product.key === ProductTemplateKey.DISCOUNT) {
          return true;
        }
        return false;
      });
      if (selectedItem) {
        setDiscountTab(selectedItem?.status === 'enabled' ? true : false);

        const configuration = selectedItem.configuration;
        setDiscount(configuration.discount_percentage ? configuration.discount_percentage * 100 : 0);
      } else {
        const configuration = defaultTemplate.configuration;
        setDiscount(configuration.discount_percentage ? configuration.discount_percentage * 100 : 0);
      }
      setEnableSaveOption(false);
    }
  }, [packageData]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = () => {
    const payload: Partial<PackageProduct> = {
      status: discountTab ? 'enabled' : 'disabled',
      configuration: {
        discount_percentage: +(discount / 100).toFixed(2),
      },
    };
    if (Validation()) {
      if (selectedPackageProduct) {
        updatePackageProduct.mutate(payload);
      } else {
        createPackageProduct.mutate(payload);
      }
    }
  };

  return (
    <AccordionTile
      setStatus={(data: boolean) => {
        if ((!data && Validation()) || data) {
          !enableSaveOption && setEnableSaveOption(true);
          setDiscountTab(data);
        }
      }}
      Label={'DISCOUNT'}
      buttonText={'SAVE'}
      status={discountTab}
      id={'discount'}
      initialstatus={true}
      disableButton={enableSaveOption}
      onButtonClick={() => {
        if (enableSaveOption) onSave();
      }}
      InnerComponent={
        <div className="w-1/2 px-2 py-4">
          <CustomInput
            inputType={'number'}
            value={discount}
            setValue={(data) => {
              !enableSaveOption && setEnableSaveOption(true);
              setDiscount(data);
            }}
            Label={'Discount Rate (%)'}
            Error={error?.length > 0 ? true : false}
            ErrorMessage={error}
            dataTestId="discount-rate"
          />
        </div>
      }
    />
  );
};

export default Discount;
